import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Star, MapPin } from "lucide-react";
import { useLang } from "../i18n";

export const VerifiedBadge = ({ small }) => {
  const { t } = useLang();
  return (
    <span data-testid="verified-chef-badge" className={`inline-flex items-center gap-1.5 bg-gold/15 border border-gold/30 text-gold ${small ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1"} uppercase tracking-wider`}>
      <BadgeCheck size={small ? 11 : 13} /> {t("common.verified")}
    </span>
  );
};

export default function ChefCard({ chef, index = 0 }) {
  const { t, lang } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/${lang}/chef/${chef.slug}`} data-testid={`chef-card-${chef.slug}`} className="group block bg-coal border border-white/8 hover:border-gold/40 transition-colors duration-500">
        <div className="relative overflow-hidden aspect-[4/5]">
          <img src={chef.photo} alt={chef.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute top-4 start-4"><VerifiedBadge small /></div>
          {chef.featured && (
            <span className="absolute top-4 end-4 bg-black/70 text-gold text-[10px] uppercase tracking-widest px-2 py-1 border border-gold/30">★</span>
          )}
          <div className="absolute bottom-0 inset-x-0 p-5">
            <h3 className="font-serif text-xl sm:text-2xl text-ivory">{chef.name}</h3>
            <p className="text-xs text-neutral-400 mt-1 flex items-center gap-1.5">
              <MapPin size={11} className="text-gold" /> {chef.city} · {t("common.yearsExp", { n: chef.years_experience })}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-4">
          <span className="text-sm text-neutral-300">
            <span className="text-gold font-serif text-lg">€{chef.price_per_guest}</span>{" "}
            <span className="text-neutral-500 text-xs">{t("common.perGuest")}</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm text-neutral-300">
            <Star size={13} className="text-gold fill-gold" /> {Number(chef.rating).toFixed(1)}
            <span className="text-neutral-600 text-xs">({chef.review_count})</span>
          </span>
        </div>
      </Link>
    </motion.div>
  );
}
